// @ts-check
import apiClient from '../middleware/apiClient';
import { INSTALLMENT_PLANS, ONE_TIME_PAY } from '../helpers/apis';
import { DEFAULT_INSTALLMENT_PLAN_DEPOSIT } from '../helpers/installmentPlans';
import { sendChannelTracking } from './channelTracking';
import { LTIP_COMPLETED, LTIP_FAILED } from '../helpers/channelTracking';

/**
 * Action type constants for installment plans
 */
export const GET_INSTALLMENT_PLANS_ELIGIBILITY = 'GET_INSTALLMENT_PLANS_ELIGIBILITY';
export const GET_INSTALLMENT_PLANS_ELIGIBILITY_SUCCESS = 'GET_INSTALLMENT_PLANS_ELIGIBILITY_SUCCESS';
export const GET_INSTALLMENT_PLANS_ELIGIBILITY_FAILURE = 'GET_INSTALLMENT_PLANS_ELIGIBILITY_FAILURE';
export const REVIEW_INSTALLMENT_PLAN = 'REVIEW_INSTALLMENT_PLAN';
export const SUBMIT_INSTALLMENT_PLAN_PAYMENT = 'SUBMIT_INSTALLMENT_PLAN_PAYMENT';
export const SUBMIT_INSTALLMENT_PLAN_PAYMENT_SUCCESS = 'SUBMIT_INSTALLMENT_PLAN_PAYMENT_SUCCESS';
export const SUBMIT_INSTALLMENT_PLAN_PAYMENT_FAILURE = 'SUBMIT_INSTALLMENT_PLAN_PAYMENT_FAILURE';
export const SUBMIT_INSTALLMENT_PLAN_ENROLLMENT = 'SUBMIT_INSTALLMENT_PLAN_ENROLLMENT';
export const SUBMIT_INSTALLMENT_PLAN_ENROLLMENT_FAILURE = 'SUBMIT_INSTALLMENT_PLAN_ENROLLMENT_FAILURE';
export const SUBMIT_INSTALLMENT_PLAN_CONFIRMATION = 'SUBMIT_INSTALLMENT_PLAN_CONFIRMATION';

/**
 * @import {
 *   ReviewData,
 *   EnrollmentData,
 *   TransformedCpcData
 * } from '../types/state/installmentPlansState'
 */

export const getInstallmentPlansEligibility = (
  deposit = DEFAULT_INSTALLMENT_PLAN_DEPOSIT,
) => async (dispatch) => {
  try {
    // Validate and coerce deposit to a non-negative number
    const numDeposit = Number(deposit);
    const depositAmount = Number.isFinite(numDeposit)
      ? Math.max(0, numDeposit)
      : DEFAULT_INSTALLMENT_PLAN_DEPOSIT;
    const params = new URLSearchParams({ deposit: String(depositAmount) });
    const url = `${INSTALLMENT_PLANS}?${params}`;
    const response = await dispatch({
      type: GET_INSTALLMENT_PLANS_ELIGIBILITY,
      payload: apiClient.fetch(url),
    }).payload;

    dispatch({
      type: GET_INSTALLMENT_PLANS_ELIGIBILITY_SUCCESS,
      payload: response,
    });
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error));
    dispatch({
      type: GET_INSTALLMENT_PLANS_ELIGIBILITY_FAILURE,
      payload: {
        message: err.message,
      },
    });
  }
};

/**
 * Redux action creator: dispatches review data for installment plan.
 * @param {ReviewData} reviewData - Review data with payment and plan info
 * @returns {function} Redux thunk that dispatches REVIEW_INSTALLMENT_PLAN
 */
export const dispatchSetInstallmentReviewData = reviewData => (dispatch) => {
  dispatch({
    type: REVIEW_INSTALLMENT_PLAN,
    payload: reviewData,
  });
};

/**
 * Transform billing address from component format to API format.
 * Component format: { firstName, lastName, address: { address1, address2, city, state, zip } }
 * API format: { person: { firstName, lastName }, address: { addressLine1, addressLine2, ... } }
 * @param {Object} billingAddress - Billing address in component format
 * @returns {Object} Billing address in API format
 */
const transformBillingAddressToApi = (billingAddress) => {
  if (!billingAddress) return billingAddress;

  const { firstName, lastName, address = {} } = billingAddress;
  return {
    person: { firstName, lastName },
    address: {
      addressLine1: address.address1,
      addressLine2: address.address2,
      city: address.city,
      state: address.state,
      zip: address.zip,
    },
  };
};

/**
 * Submit one-time payment for installment plan (ONE_TIME_PAY API).
 * Only called if deposit amount > 0.
 * Data is pre-transformed in AddInstrument via cpcDataToPaymentFormData
 * before reaching this action.
 * @param {TransformedCpcData} paymentData - Pre-transformed payment data
 *   with token, billingAddress, etc.
 * @param {number} depositAmount - The deposit amount to add to payload
 * @returns {(dispatch: Function) => Promise<Object>} Redux thunk that
 *   returns payment response
 */
export const submitInstallmentPayment = (paymentData, depositAmount) => async (dispatch) => {
  try {
    if (!paymentData || !paymentData.token) {
      throw new Error('Payment data is missing required token');
    }

    if (!paymentData.billingAddress) {
      throw new Error('Payment data is missing required billing address');
    }

    // Build API payload from pre-transformed payment data
    // Key order matters for test string comparison
    const payload = {
      amount: depositAmount,
      billingAddress: transformBillingAddressToApi(paymentData.billingAddress),
      token: paymentData.token,
      walletId: paymentData.walletId,
    };

    const response = await dispatch({
      type: SUBMIT_INSTALLMENT_PLAN_PAYMENT,
      payload: apiClient.fetch(ONE_TIME_PAY, { method: 'POST', body: JSON.stringify(payload) }),
    }).payload;

    dispatch({
      type: SUBMIT_INSTALLMENT_PLAN_PAYMENT_SUCCESS,
      payload: response,
    });

    return response;
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error));
    dispatch({
      type: SUBMIT_INSTALLMENT_PLAN_PAYMENT_FAILURE,
      payload: {
        message: err.message,
      },
    });
    throw error;
  }
};

/**
 * Redux action creator: submits enrollment data for LTIP enrollment.
 * @param {EnrollmentData} enrollmentData - Enrollment data with ltipOfferId
 * @returns {(dispatch: Function) => Promise<Object>} Redux thunk
 */
export const submitInstallmentEnrollment = enrollmentData => async (dispatch) => {
  try {
    // TODO: Build full enrollment payload with billerAccountNumber, enrollmentType, enrollInBoB
    // For now, send minimal payload with ltipOfferId
    const enrollmentPayload = { ltipOfferId: enrollmentData.ltipOfferId };

    const response = await dispatch({
      type: SUBMIT_INSTALLMENT_PLAN_ENROLLMENT,
      payload: apiClient.fetch(INSTALLMENT_PLANS, {
        method: 'POST',
        body: JSON.stringify(enrollmentPayload),
        headers: { 'Content-Type': 'application/json' },
      }),
    }).payload;

    // Dispatch confirmation with enrollment response
    dispatch({
      type: SUBMIT_INSTALLMENT_PLAN_CONFIRMATION,
      payload: response,
    });

    dispatch(sendChannelTracking({
      id: LTIP_COMPLETED,
      interactionType: 'BILLING_PAYMENTS',
    }));

    return response;
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error));
    dispatch(sendChannelTracking({
      id: LTIP_FAILED,
      error: err,
      interactionType: 'BILLING_PAYMENTS',
    }));

    dispatch({
      type: SUBMIT_INSTALLMENT_PLAN_ENROLLMENT_FAILURE,
      payload: {
        message: err.message,
      },
    });
    throw error;
  }
};
